/**
 * UserInviteResetCard — admin widget to invite or reset CRM users that
 * are NOT in the `agents` collection (e.g. Eufemia, Morena, customer-care
 * managers). Uses POST /api/auth/send-setup-links which:
 *   • optionally resets the user's current password (random hash)
 *   • generates a 7-day HMAC magic link to /setup-account/<token>
 *   • emails the link via Resend (also returned in the response so the
 *     admin can hand-copy it if the email gets stuck in spam).
 *
 * ★ Fase 59 — estratto da AgentsPage.jsx (split componenti oversize).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../lib/logger';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Loader2, Mail, KeyRound, Copy, CheckCircle2, AlertTriangle,
} from 'lucide-react';

export function UserInviteResetCard() {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState('');
  const [resetPasswords, setResetPasswords] = useState(true);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null); // [{email, status, link, name}]

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const emails = emailInput
      .split(/[\s,;]+/)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    if (emails.length === 0) {
      toast.error(t('inviteCard.emailRequired', 'Inserisci almeno una email'));
      return;
    }
    setSending(true);
    setResults(null);
    try {
      const { data } = await api.post('/auth/send-setup-links', {
        emails,
        reset_passwords: resetPasswords,
        // ★ Fase 20 — il link deve puntare all'ambiente da cui viene generato
        // (preview → preview, produzione → produzione)
        base_url: window.location.origin,
      });
      setResults(data?.results || []);
      const sent = (data?.results || []).filter((r) => r.status === 'sent').length;
      if (sent === emails.length) {
        toast.success(t('inviteCard.linkSent', { defaultValue: 'Link inviato a {{count}} utente/i.', count: sent }));
      } else if (sent > 0) {
        toast.warning(t('inviteCard.partialSent', { defaultValue: '{{sent}}/{{total}} inviati. Vedi dettagli sotto.', sent, total: emails.length }));
      } else {
        toast.error(t('inviteCard.noneSent', 'Nessun link inviato. Controlla i dettagli.'));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('inviteCard.sendError', 'Errore invio');
      toast.error(`${t('inviteCard.error', 'Errore')}: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('inviteCard.linkCopied', 'Link copiato'));
    } catch (err) {
      logger.debug('UserInviteResetCard clipboard copy failed', err);
      toast.error(t('inviteCard.copyFailed', 'Copia non riuscita, copia manualmente'));
    }
  };

  return (
    <div
      className="rounded-xl border border-[var(--gp-gold)]/30 bg-gradient-to-br from-[var(--gp-gold)]/8 to-white p-4 md:p-5 space-y-3"
      data-testid="user-invite-reset-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-[var(--gp-gold)]/15 grid place-items-center text-[var(--gp-forest)] shrink-0">
          <KeyRound className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-base md:text-lg text-[hsl(var(--primary))] leading-tight">
            {t('inviteCard.title', 'Inviti & Reset user CRM')}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('inviteCard.subtitle', 'Genera un magic-link di primo accesso o reset password per un utente CRM (admin, legale, customer care — anche se non è in lista agenti).')}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t('inviteCard.emailLabel', 'Email destinatario (puoi inserirne più d\u2019una separate da virgola)')}
          </Label>
          <Input
            type="text"
            placeholder="eufemia@housecashflows.com"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            className="mt-1"
            disabled={sending}
            data-testid="user-invite-email-input"
          />
        </div>
        <div className="flex items-center gap-2 px-2 h-10 rounded-md bg-white border border-border">
          <Switch
            id="reset-pw-switch"
            checked={resetPasswords}
            onCheckedChange={setResetPasswords}
            disabled={sending}
            data-testid="user-invite-reset-switch"
          />
          <Label htmlFor="reset-pw-switch" className="text-xs cursor-pointer">
            {t('inviteCard.resetPw', 'Resetta password')}
          </Label>
        </div>
        <Button
          type="submit"
          disabled={sending || !emailInput.trim()}
          className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)] h-10"
          data-testid="user-invite-send-btn"
        >
          {sending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('inviteCard.sending', 'Invio…')}</>
          ) : (
            <><Mail className="w-4 h-4 mr-2" /> {t('inviteCard.sendSetupLink', 'Invia link setup')}</>
          )}
        </Button>
      </form>

      {results && results.length > 0 && (
        <div
          className="rounded-lg border border-border bg-white p-3 space-y-2"
          data-testid="user-invite-results"
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            {t('inviteCard.sendResult', 'Risultato invio')}
          </div>
          {results.map((r, i) => {
            const ok = r.status === 'sent';
            return (
              <div key={r.email || i} className="flex items-start gap-2 text-xs">
                <div className={`mt-0.5 w-4 h-4 rounded-full grid place-items-center shrink-0 ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {ok ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">{r.email}</div>
                  <div className={`text-[11px] ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {ok
                      ? `✓ ${t('inviteCard.emailSentOk', 'Email inviata. Magic link valido 7 giorni.')}`
                      : `⚠ ${r.status || t('inviteCard.unknownError', 'errore sconosciuto')}`}
                  </div>
                  {r.link && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
                      <code className="text-[10px] bg-[var(--surface-2)] px-1.5 py-0.5 rounded truncate max-w-[280px] md:max-w-[400px] flex-1">
                        {r.link}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyLink(r.link)}
                        className="p-1 hover:bg-[var(--surface-2)] rounded"
                        title={t('inviteCard.copyLink', 'Copia link')}
                        data-testid={`user-invite-copy-${i}`}
                      >
                        <Copy className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div className="text-[10px] text-muted-foreground italic pt-1">
            {t('inviteCard.spamNote', 'Se l\u2019email non arriva entro 2-3 minuti, controlla la cartella spam o copia il link e inoltralo a mano dall\u2019icona qui sopra.')}
          </div>
        </div>
      )}
    </div>
  );
}
